<template>
  <div class="jobs-page">
    <!-- Hero Section -->
    <div class="page-hero">
      <div class="hero-bg"></div>
      <div class="hero-content">
        <h1 class="hero-title">{{ $t('jobs.title') }}</h1>
      </div>
    </div>

    <div class="container">
      <!-- Introduction section -->
      <div class="intro-section">
        <p class="intro-text">{{ $t('jobs.intro') }}</p>
      </div>

      <!-- Advantages Grid -->
      <div class="advantages-section">
        <h2 class="section-title">{{ $t('jobs.advantagesTitle') }}</h2>
        <div class="advantages-grid">
          <!-- 这里使用硬编码循环 10 次，因为我们在多语言中设置了 10 条优势 -->
          <div class="adv-card" v-for="i in 10" :key="i">
            <div class="adv-icon">
              <i class="el-icon-finished"></i>
            </div>
            <div class="adv-text">{{ $t(`jobs.advantages.${i - 1}`) }}</div>
          </div>
        </div>
      </div>

      <!-- Positions List with Collapse -->
      <div class="positions-section">
        <h2 class="section-title">{{ $t('jobs.positionsTitle') }}</h2>
        
        <el-collapse v-model="activeNames" class="custom-collapse">
          <el-collapse-item v-for="(job, index) in jobCount" :key="index" :name="index">
            <template slot="title">
              <div class="job-title-wrapper">
                <h3>{{ $t(`jobs.positions.${index}.title`) }}</h3>
                <span class="job-overview">{{ $t(`jobs.positions.${index}.overview`) }}</span>
              </div>
            </template>
            
            <div class="job-detail-content">
              <!-- Expectations -->
              <div class="detail-block">
                <h4>{{ $t(`jobs.positions.${index}.expectTitle`) }}</h4>
                <ul class="styled-list">
                  <li v-for="i in 10" :key="'expect-'+i">
                    <!-- 由于每个岗位的期望条数基本都是 10 条，这里循环 10 次 -->
                    <i class="el-icon-check"></i>
                    {{ $t(`jobs.positions.${index}.expectations.${i - 1}`) }}
                  </li>
                </ul>
              </div>

              <!-- Software Requirements (If any) -->
              <div class="detail-block" v-if="$t(`jobs.positions.${index}.softwareTitle`)">
                <h4>{{ $t(`jobs.positions.${index}.softwareTitle`) }}</h4>
                <div class="software-tags">
                  <span class="tech-tag" v-for="i in 10" :key="'soft-'+i">
                    {{ $t(`jobs.positions.${index}.software.${i - 1}`) }}
                  </span>
                </div>
              </div>

              <!-- Responsibilities (If any) -->
              <div class="detail-block" v-if="$t(`jobs.positions.${index}.responsibilitiesTitle`)">
                <h4>{{ $t(`jobs.positions.${index}.responsibilitiesTitle`) }}</h4>
                <ul class="styled-list">
                  <li v-for="i in 3" :key="'resp-'+i">
                    <i class="el-icon-caret-right"></i>
                    {{ $t(`jobs.positions.${index}.responsibilities.${i - 1}`) }}
                  </li>
                </ul>
              </div>

              <div class="apply-action">
                <el-button type="primary" class="apply-btn" @click="handleApply">
                  {{ $t('jobs.applyNow') }}
                </el-button>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'Jobs',
  data() {
    return {
      activeNames: [0] // 默认展开第一个岗位
    }
  },
  computed: {
    jobCount() {
      try {
        return this.$i18n.messages[this.$i18n.locale].jobs.positions.length
      } catch (e) {
        return 4
      }
    }
  },
  methods: {
    handleApply() {
      // 提供一个基本的应聘反馈，后续可以对接真实的投递逻辑
      const email = (this.$brand && this.$brand.id === 'mt')
        ? 'hr@mindset-software.com'
        : 'hr@lpt-corporation.com'
      this.$message({
        message: `Thank you for your interest! Please send your CV to ${email}`,
        type: 'success',
        duration: 4000
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.jobs-page {
  min-height: 100vh;
  background: #f8fafc;
  padding-bottom: 80px;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ==================================================
   Hero Section
   ================================================== */
.page-hero {
  position: relative;
  height: 380px; // Slightly shorter than company hero
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding-top: 70px; 
  overflow: hidden;

  .hero-bg {
    position: absolute;
    inset: 0;
    // Darker corporate tech gradient
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    
    // Abstract shapes / mesh overlay
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background-image: radial-gradient(circle at 10% 20%, rgb(15 180 255) 0%, #4f90ff 40%), radial-gradient(circle at 90% 80%, rgb(56 189 248) 0%, transparent 40%)
    }
  }

  .hero-content {
    position: relative;
    z-index: 2;
    animation: slideUp 0.8s ease-out;

    .hero-title {
      font-size: 52px;
      font-weight: 800;
      margin: 0;
      letter-spacing: -1px;
    }
  }
}

/* ==================================================
   Content Sections
   ================================================== */
.intro-section {
  background: white;
  padding: 40px;
  border-radius: 16px;
  margin-top: -50px; // Pull up over the hero
  position: relative;
  z-index: 10;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  margin-bottom: 60px;

  .intro-text {
    font-size: 16px;
    color: #475569;
    line-height: 1.8;
    text-align: center;
    margin: 0;
  }
}

.section-title {
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  text-align: center;
  margin-bottom: 40px;
}

/* ==================================================
   Advantages Grid
   ================================================== */
.advantages-section {
  margin-bottom: 80px;

  .advantages-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;

    .adv-card {
      background: white;
      padding: 24px;
      border-radius: 12px;
      display: flex;
      align-items: flex-start;
      box-shadow: 0 4px 12px rgba(0,0,0,0.02);
      border: 1px solid #f1f5f9;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px rgba(14, 165, 233, 0.1);
        border-color: #e0f2fe;
        
        .adv-icon {
          background: #0ea5e9;
          color: white;
        }
      }

      .adv-icon {
        width: 36px;
        height: 36px;
        background: #f1f5f9;
        color: #0ea5e9;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        flex-shrink: 0;
        margin-right: 16px;
        transition: all 0.3s ease;
      }

      .adv-text {
        font-size: 14px;
        color: #475569;
        line-height: 1.5;
        font-weight: 500;
        margin-top: 6px; // Align roughly with icon center visually
      }
    }
  }
}

/* ==================================================
   Positions Collapse Component
   ================================================== */
.positions-section {
  .custom-collapse {
    border: none;

    ::v-deep .el-collapse-item {
      background: white;
      margin-bottom: 16px;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0,0,0,0.02);
      transition: all 0.3s;

      &.is-active {
        border-color: #bae6fd;
        box-shadow: 0 8px 24px rgba(14, 165, 233, 0.08);
      }
      
      .el-collapse-item__header {
        height: auto;
        line-height: 1.4;
        padding: 24px;
        border-bottom: none;
        background: transparent;
        font-size: 18px;
        color: #1e293b;

        &.is-active {
          border-bottom: 1px solid #f1f5f9;
        }
      }

      .el-collapse-item__wrap {
        border-bottom: none;
      }

      .el-collapse-item__content {
        padding: 24px;
        padding-bottom: 32px;
      }
    }
  }

  .job-title-wrapper {
    display: flex;
    flex-direction: column;

    h3 {
      margin: 0 0 8px;
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
    }
    .job-overview {
      font-size: 14px;
      color: #64748b;
      font-weight: normal;
    }
  }

  .job-detail-content {
    .detail-block {
      margin-bottom: 32px;

      h4 {
        font-size: 16px;
        font-weight: 700;
        color: #1e293b;
        margin: 0 0 16px;
      }

      .styled-list {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          position: relative;
          padding-left: 28px;
          margin-bottom: 12px;
          color: #475569;
          font-size: 15px;
          line-height: 1.6;

          i {
            position: absolute;
            left: 0;
            top: 4px;
            color: #10b981; // Green check
            font-size: 16px;
            
            &.el-icon-caret-right {
              color: #0ea5e9; // Blue arrow for responsibilities
            }
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .software-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .tech-tag {
          background: #f1f5f9;
          color: #334155;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;

          &:hover {
            background: #e0f2fe;
            color: #0284c7;
            border-color: #bae6fd;
          }
        }
      }
    }

    .apply-action {
      margin-top: 40px;
      text-align: left;

      .apply-btn {
        background: linear-gradient(135deg, #0649b8 0%, #0a7fd7 100%);
        border: none;
        padding: 12px 36px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(10, 127, 215, 0.3);
        transition: all 0.3s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(10, 127, 215, 0.4);
        }
      }
    }
  }
}

// Keyframes
@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

// Mobile Responsive
@media (max-width: 768px) {
  .page-hero .hero-content .hero-title {
    font-size: clamp(28px, 7vw, 36px);
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .section-title {
    font-size: clamp(22px, 5.5vw, 28px);
    line-height: 1.4;
  }

  .custom-collapse .job-title-wrapper {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .intro-section { padding: 24px; text-align: left; .intro-text {text-align: left;} }
  .advantages-section .advantages-grid { grid-template-columns: 1fr; }
}
</style>
